// Shapes internal_tasks rows and clinical_action_items (Orders) into one list
// for the Messages modal's Tasks tab. Pure — no fetching — so it is testable.

import { normalizeStatus } from "../../utils/workItemStatus";
import { normalizePriority, priorityRank } from "../../utils/taskPriority";

export function taskToItem(task) {
  const status = normalizeStatus(task.status);
  return {
    key: `task:${task.id}`,
    id: task.id,
    kind: "task",
    schema: null,
    status,
    title: task.title,
    description: task.description || null,
    patientName: null,
    assignedTo: task.assigned_to || null,
    assignedBy: task.assigned_by || null,
    assigneeName: task.assigned_to_name || null,
    assigneeEmail: task.assigned_to_email || null,
    assigneeSchema: task.assigned_to_schema || null,
    assignerName: task.assigned_by_name || null,
    assignerSchema: task.assigned_by_schema || null,
    due: task.due_date || null,
    priority: normalizePriority(task.priority),
    visitDate: null,
    // "memory_issue" / "db_health": made from a SuperAdmin → Live Activity row
    // (the Tasks tab shows a badge for it).
    sourceKind: task.source_kind || null,
    // What it was made from, e.g. an order form's "<visitId>:<formId>" (the
    // task shows that form), and the schema of the chart it came from.
    sourceRef: task.source_ref || null,
    sourceSchema: task.source_doc_schema || null,
    // A task made from something other than a note (its badge says what)
    // just names its patient.
    sourceLabel: task.source_patient_name
      ? [task.source_kind ? null : "From note", task.source_patient_name, task.source_visit_date].filter(Boolean).join(" · ")
      : null,
    done: status === "completed",
    rejected: status === "rejected",
    cancelled: status === "cancelled",
    createdAt: task.created_at || null,
    completedAt: task.completed_at || null,
    updatedAt: task.updated_at || null,
  };
}

// fetchOrders() groups items per patient; only assigned items are someone's task.
// `assigned_to` from the orders API is an object `{name, email}` (or null).
// A row tagged with `_schema` (the schema it was read from) keeps that schema,
// so its transitions go back to the right table; `schema` is the fallback.
export function ordersToItems(patients, schema) {
  const items = [];
  for (const patient of patients || []) {
    for (const order of patient.items || []) {
      if (!order.assigned_to_user_id) continue;
      const status = normalizeStatus(order.status);
      items.push({
        key: `order:${order.id}`,
        id: order.id,
        kind: "order",
        schema: order._schema || schema,
        status,
        orderType: order.item_type || "order",
        title: order.description,
        description: null,
        patientName: patient.patient_name || null,
        assignedTo: order.assigned_to_user_id,
        assignedBy: order.assigned_by_user_id || null,
        assigneeName: order.assigned_to?.name || order.assigned_to?.email || null,
        assigneeEmail: order.assigned_to?.email || null,
        assigneeSchema: order.assigned_to?.schema || null,
        assignerName: null,
        assignerSchema: null,
        due: null,
        priority: normalizePriority(order.priority),
        visitDate: order.scheduled_date || null,
        sourceKind: null,
        sourceLabel: null,
        done: status === "completed",
        rejected: status === "rejected",
        cancelled: false,
        createdAt: order.assigned_at || order.created_at || null,
        completedAt: order.completed_at || null,
        updatedAt: order.updated_at || null,
      });
    }
  }
  return items;
}

// Combine one Tasks-tab reload with the previous one. `tasksRes` and each of
// `ordersRes` (one per entry in `schemas`, own schema first) are
// Promise.allSettled results. A source that failed — rejected, or an orders
// payload flagged `_error` — keeps its previous rows instead of emptying the
// list. Orders are tagged with the schema they were read from and deduped by
// id, the own-schema copy winning.
// prev/returned sources: { tasks: item[], orders: { [schema]: item[] } }
export function mergeTaskSources(prev, schemas, tasksRes, ordersRes) {
  const sources = { tasks: prev?.tasks || [], orders: { ...(prev?.orders || {}) } };
  if (tasksRes?.status === "fulfilled") sources.tasks = (tasksRes.value?.tasks || []).map(taskToItem);
  schemas.forEach((schema, i) => {
    const res = ordersRes[i];
    if (res?.status !== "fulfilled" || res.value?._error) return;
    const patients = (res.value?.patients || []).map((g) => ({
      ...g,
      items: (g.items || []).map((it) => ({ ...it, _schema: schema })),
    }));
    sources.orders[schema] = ordersToItems(patients, schema);
  });
  const seen = new Set();
  const orders = schemas.flatMap((schema) => sources.orders[schema] || []).filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
  return { sources, items: [...sources.tasks, ...orders] };
}

// What the 📋 picker in a chat may link: only the user's own tasks and orders
// (assigned to or by them). The loaded orders include everyone's assigned
// orders in the schema and public; another person's order, with its
// patient's name, must never be offered.
export function linkableItems(items, userId) {
  return (items || []).filter((i) => !i.cancelled && (i.assignedTo === userId || i.assignedBy === userId));
}

// Is this item inside a channel: both the person it's assigned to and the
// person who assigned it are members? memberIds: a Set of ids, or an array.
export function inChannel(item, memberIds) {
  const ids = memberIds instanceof Set ? memberIds : new Set(memberIds || []);
  return ids.has(item.assignedTo) && ids.has(item.assignedBy);
}

// The people half of a view: "mine" = assigned to me, "assigned" = by me,
// "channel" = assigned to and by members of a channel (memberIds).
export function inView(item, userId, view, memberIds) {
  if (view === "channel") return inChannel(item, memberIds);
  return view === "assigned" ? item.assignedBy === userId : item.assignedTo === userId;
}

// Earliest due date first (items with one before items without); then
// priority (high first); then newest first.
export function compareByDueThenPriority(a, b) {
  if (a.due && b.due && a.due !== b.due) return a.due < b.due ? -1 : 1;
  if (!!a.due !== !!b.due) return a.due ? -1 : 1;
  const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
  if (byPriority) return byPriority;
  return (b.createdAt || "").localeCompare(a.createdAt || "");
}

// Board columns: priority first (high, then medium, then low), then the same
// due-date / newest-first order as the list.
export function compareByPriorityThenDue(a, b) {
  return priorityRank(a.priority) - priorityRank(b.priority) || compareByDueThenPriority(a, b);
}

// The item after a status change, with its flags — stamped the way the
// backend stamps the row (updated now; completed now only when completed), so
// a card just dropped on Recently completed stays there until the reload.
export function withStatus(item, raw, now = new Date()) {
  const status = normalizeStatus(raw);
  const at = now.toISOString();
  return {
    ...item,
    status,
    done: status === "completed",
    rejected: status === "rejected",
    cancelled: status === "cancelled",
    updatedAt: at,
    completedAt: status === "completed" ? at : null,
  };
}

// view: "mine" (assigned to me) | "assigned" (assigned by me) | "channel"
// (assigned to and by members of a channel: memberIds).
// Ordered by compareByDueThenPriority. Closed items (completed or cancelled)
// show only with "Show completed", below the open ones.
export function filterItems(items, { userId, view, showDone, memberIds }) {
  const members = new Set(memberIds || []);
  const picked = (items || []).filter((item) => {
    if (!showDone && (item.done || item.cancelled)) return false;
    // A rejected item is closed for the person who rejected it, and waits on the assigner.
    if (!showDone && item.rejected && view === "mine") return false;
    return inView(item, userId, view, members);
  });
  const closed = (i) => Boolean(i.done || i.cancelled);
  return picked.sort((a, b) => (closed(a) !== closed(b) ? (closed(a) ? 1 : -1) : compareByDueThenPriority(a, b)));
}

// Items waiting on ME to act. pending_review is excluded: it's waiting on the
// assigner (countNeedsMyReview covers that side).
export function countOpenForMe(items, userId) {
  return (items || []).filter(
    (i) => i.assignedTo === userId && !i.done && !i.cancelled && !i.rejected && i.status !== "pending_review",
  ).length;
}

// Items I assigned out that now wait on me: a review, or a rejection to
// reassign or close.
export function countNeedsMyReview(items, userId) {
  return (items || []).filter(
    (i) => i.assignedBy === userId && i.assignedTo !== userId && (i.status === "pending_review" || i.rejected),
  ).length;
}

// A description past a few lines (e.g. a task made from a long chat message,
// which keeps the whole message) starts collapsed in the Tasks list.
export function isLongDescription(text) {
  const s = String(text || "");
  return s.split("\n").length > 3 || s.length > 240;
}

// Clipboard text for a row's Copy button — title, then a blank line and the
// description if there is one.
export function copyTextFor(item) {
  return [item.title, item.description].filter((s) => s && String(s).trim()).join("\n\n");
}

// "2026-09-30" → true when that date is before today (local).
export function isOverdue(due, today = new Date()) {
  if (!due) return false;
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return due.slice(0, 10) < `${y}-${m}-${d}`;
}
